import * as React from 'react';
import { useMember } from '../../hooks/useMember';
import { useUpdateDispatchStatus } from '../../hooks/useUpdateDispatchStatus';
import { useUpdateDriverStatus } from '../../hooks/useUpdateDriverStatus';
import type { CallRecord } from './callRecord';

/**
 * Start a call: DISPATCHED, and the driver on duty with it. The call's own
 * Start and the assigned-call prompt's Start Call both come here, so the two
 * cannot start a call differently.
 */
export function useStartCall() {
  const member = useMember();
  const updateStatus = useUpdateDispatchStatus();
  const updateDuty = useUpdateDriverStatus();
  const [error, setError] = React.useState<string | null>(null);

  /** True once the call is started; false with `error` said in words. */
  const start = async (call: Pick<CallRecord, 'id' | 'version'>) => {
    setError(null);
    try {
      await updateStatus.mutateAsync({
        dispatchId: call.id,
        status: 'DISPATCHED',
        version: call.version,
      });
    } catch (failure) {
      setError(
        (failure as Error).message?.includes('Version conflict')
          ? 'This call was just changed by someone else. Try again.'
          : 'The call could not be started.'
      );
      return false;
    }
    // On duty with the call, as the old screen did. Without the key the
    // server refuses it, so it is not asked; failing it does not undo the
    // start.
    if (member.drives && member.can('drivers.edit_status')) {
      updateDuty.mutateAsync('ACTIVE').catch(() => {});
    }
    return true;
  };

  return { start, starting: updateStatus.isPending, error };
}
